import { Alert, Box, Button, CircularProgress, LinearProgress, Stack, Typography, alpha, useTheme } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import type { ProductCreationOperation, ProductCreationOperationPhase } from "../newProduct/productCreationOperationsApi";
import { isActiveProductCreationOperation } from "../newProduct/productCreationOperationsApi";

interface PendingCreationCardProps {
    operation: ProductCreationOperation;
    onRetry: (operation: ProductCreationOperation) => void;
    onReturnToDraft: (operation: ProductCreationOperation) => void;
    retrying?: boolean;
    returningToDraft?: boolean;
}

const PHASE_LABELS: Record<ProductCreationOperationPhase, string> = {
    validating_draft: "Validating product context",
    provisioning_product: "Creating product workspace",
    registering_build: "Starting product processing",
    processing: "Processing product context"
};

const statusLabel = (operation: ProductCreationOperation): string => {
    if (operation.status === "failed") {
        return operation.project_id == null ? "Creation failed" : "Product created, but processing needs attention";
    }
    if (operation.status === "canceled") return "Returned to draft";
    if (operation.status === "succeeded") return "Product created";
    return PHASE_LABELS[operation.phase] ?? "Creating product";
};

const PendingCreationCard = ({ operation, onRetry, onReturnToDraft, retrying = false, returningToDraft = false }: PendingCreationCardProps) => {
    const theme = useTheme();
    const active = isActiveProductCreationOperation(operation);
    const failed = operation.status === "failed";
    const failedBeforeProduct = failed && operation.project_id == null;
    const canRetry = failed && operation.retryable;
    const canReturn = failedBeforeProduct && operation.can_return_to_draft;

    return (
        <Box
            aria-label={`Product creation ${operation.working_name}`}
            sx={{
                borderRadius: 1.5,
                border: `1px solid ${failed ? alpha(theme.palette.error.main, 0.45) : alpha(theme.palette.primary.main, 0.3)}`,
                background: theme.palette.background.paper,
                p: 2,
                minHeight: 178,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.06)}`
            }}
        >
            <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        color: failed ? theme.palette.error.main : theme.palette.primary.main,
                        backgroundColor: alpha(failed ? theme.palette.error.main : theme.palette.primary.main, 0.09)
                    }}
                >
                    <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: 14.5,
                            fontWeight: 650,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {operation.working_name}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: failed ? "error.main" : "text.secondary", mt: 0.25 }}>{statusLabel(operation)}</Typography>
                </Box>
                <Box
                    sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 4,
                        color: failed ? "error.main" : "primary.main",
                        bgcolor: alpha(failed ? theme.palette.error.main : theme.palette.primary.main, 0.09),
                        fontSize: 9.5,
                        fontWeight: 800,
                        textTransform: "uppercase"
                    }}
                >
                    {failed ? "Failed" : active ? "Creating" : operation.status}
                </Box>
            </Stack>

            {active && <LinearProgress aria-label={`Creating ${operation.working_name}`} sx={{ height: 4, borderRadius: 2 }} />}
            {operation.error && failed && (
                <Alert severity="error" sx={{ py: 0.25, "& .MuiAlert-message": { fontSize: 12.5 } }}>
                    {operation.error}
                </Alert>
            )}

            <Box sx={{ flex: 1 }} />
            {failed ? (
                <Stack direction="row" spacing={1}>
                    {canRetry && (
                        <Button
                            size="small"
                            variant="contained"
                            disabled={retrying || returningToDraft}
                            startIcon={retrying ? <CircularProgress size={13} color="inherit" /> : <ReplayRoundedIcon />}
                            onClick={() => onRetry(operation)}
                        >
                            {retrying ? "Retrying…" : "Retry"}
                        </Button>
                    )}
                    {canReturn && (
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={retrying || returningToDraft}
                            startIcon={returningToDraft ? <CircularProgress size={13} color="inherit" /> : <EditNoteOutlinedIcon />}
                            onClick={() => onReturnToDraft(operation)}
                        >
                            {returningToDraft ? "Returning…" : "Return to Draft"}
                        </Button>
                    )}
                </Stack>
            ) : (
                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                    {failed
                        ? "Build recovery will be available on the Product card as soon as it appears."
                        : "You can leave this page. Product creation will continue in the background."}
                </Typography>
            )}
        </Box>
    );
};

export default PendingCreationCard;
